import { Box, Divider, Grid, RadioGroup, Stack, TextField, Typography, useTheme } from '@mui/material';
import {
  Button,
  Card,
  Checkbox,
  CheckboxSelect,
  NestedItem,
  ButtonGroup,
  Modal,
} from '@perry-weather/component-library';
import WarningIcon from '../../Assets/NotificationIcons/WarningIcon';
import { Replay, Save } from '@mui/icons-material';
import { withClickTracking } from '../Tracked/withMixpanelTracking';
import { TrackableEvents } from '../Tracked/events';
import {
  Communication,
  CommunicationErrorModel,
  ModelStateErrorResponse,
  Site,
  User,
  useSaveTemplateMutation,
  useSendMassMessageMutation,
  useUpdateTemplateMutation,
} from '../../Services/API';
import { useEffect, useMemo, useRef, useState } from 'react';
import mixpanel from 'mixpanel-browser';
import { openToast } from '../../features/toast/toastSlice';
import { useDispatch } from 'react-redux';
import {
  EMAIL_BIT,
  PUSH_BIT,
  SMS_BIT,
  DEFAULT_CHANNEL,
  DEFAULT_OVERRIDE_PREFERENCE,
} from '../../Constants/MassMessageConstants';

interface MessageFormProps {
  currentUser: User | undefined;
  users: User[] | undefined;
  sites: Site[] | undefined;
  notiUsers: User[] | undefined;
  userData: any | undefined;
  communication: Communication;
  storedCommunication?: Communication;
  errorState: CommunicationErrorModel;
  onFormChange: (communication: Communication) => void;
  onSaveTemplate?: () => void;
  onSendMessage?: () => void;
  resetCommunication?: () => void;
  updateError?: (error: CommunicationErrorModel) => void;
  notify: (error: ModelStateErrorResponse, message: string) => void;
}

const TrackedButton = withClickTracking(Button)(TrackableEvents.ButtonClicked, {
  button_location: 'Mass Notify',
  button_feature: 'Mass Notify',
  button_type: 'button',
});

const TrackedCheckbox = withClickTracking(Checkbox)(TrackableEvents.ButtonClicked, {
  button_location: 'Mass Notify',
  button_feature: 'Mass Notify',
  button_type: 'checkbox',
});

const MessageForm = (props: MessageFormProps) => {
  const {
    currentUser,
    users,
    sites,
    notiUsers,
    userData,
    communication,
    storedCommunication,
    errorState,
    onFormChange,
    onSaveTemplate,
    onSendMessage,
    resetCommunication,
    updateError,
    notify,
  } = props;

  const theme = useTheme();
  const dispatch = useDispatch();

  const [recipOptions, setRecipOptions] = useState<NestedItem[]>([]);
  const [openSendConfirmationModal, setSendOpenConfirmationModal] = useState<boolean>(false);

  const iconProps = { height: 24, width: 24 };
  const isDarkTheme = theme.palette.mode === 'dark';
  const hasHeadlineAndMessageAndSender =
    communication.headline?.length && communication.message.length && communication.senderText?.length;

  const hasTemplateBeenModified =
    communication.headline !== storedCommunication?.headline ||
    communication.message !== storedCommunication?.message ||
    communication.senderText !== storedCommunication?.senderText ||
    communication.channel !== storedCommunication?.channel ||
    communication.overridePreferences !== storedCommunication?.overridePreferences;

  const [
    sendMassMessage,
    {
      isSuccess: isSendMassMessageSuccess,
      isLoading: isSendMassMessageLoading,
      isError: isSendMassMessageError,
      error: sendMassMessageError,
    },
  ] = useSendMassMessageMutation();
  const [
    saveTemplate,
    {
      data: saveTemplateData,
      isLoading: isSaveTemplateLoading,
      isSuccess: isSaveTemplateSuccess,
      isError: isSaveTemplateError,
      error: saveTemplateError,
    },
  ] = useSaveTemplateMutation();
  const [
    updateTemplate,
    {
      isSuccess: isUpdateTemplateSuccess,
      isLoading: isUpdateTemplateLoading,
      isError: isUpdateTemplateError,
      error: updateTemplateError,
    },
  ] = useUpdateTemplateMutation();

  const isLoading = isSendMassMessageLoading || isSaveTemplateLoading || isUpdateTemplateLoading;

  useMemo(() => {
    if (userData && userData.profile && users && sites && notiUsers) {
      let options: NestedItem[] = [];
      if (userData.profile.Role === 'Assistant Admin') {
        let currentUser = users.find(x => x.id === userData.profile.sub);
        if (currentUser) {
          options = sites
            .filter(site => site.id === currentUser!.siteId)
            .map(site => ({
              label: site.name,
              type: 'site',
              siteId: site.id,
              children: users
                .filter(
                  user =>
                    user.id !== userData?.profile?.sub && user.siteId === currentUser!.siteId && user.siteId === site.id
                )
                .map(user => ({ value: user.id, label: user.fullName, type: 'user', siteId: user.siteId }))
                .concat(
                  notiUsers
                    .filter(notiUser => notiUser.isActive && notiUser.siteId === site.id)
                    .map(notiUser => ({
                      value: notiUser.id,
                      label: notiUser.fullName,
                      type: 'notification-user',
                      siteId: notiUser.siteId,
                    }))
                ),
            }));
        }
      } else {
        options = sites
          .filter(site => users.some(u => u.siteId === site.id) || notiUsers.some(u => u.siteId === site.id))
          .map(site => ({
            label: site.name,
            type: 'site',
            siteId: site.id,
            children: users
              .filter(user => user.id !== userData.profile.sub && user.isActive && user.siteId === site.id)
              .map(user => ({ value: user.id, label: user.fullName, type: 'user', siteId: user.siteId }))
              .concat(
                notiUsers
                  .filter(notiUser => notiUser.isActive && notiUser.siteId === site.id)
                  .map(notiUser => ({
                    value: notiUser.id,
                    label: notiUser.fullName,
                    type: 'notification-user',
                    siteId: notiUser.siteId,
                  }))
              ),
          }));
      }
      options = options.filter(x => !x.children || x.children?.length !== 0);

      setRecipOptions(options);
    }
  }, [users, sites, notiUsers, userData]);

  useEffect(() => {
    if (isSendMassMessageSuccess) {
      mixpanel.track(TrackableEvents.MassNotifyCreated, {
        user: userData?.profile?.sub,
        recipients: communication.recipients,
        message: communication.message,
        channel: communication.channel,
      });
      dispatch(openToast({ variant: 'success', header: 'Message sent' }));
      resetCommunication?.();
    } else if (isSendMassMessageError) {
      notify(sendMassMessageError as ModelStateErrorResponse, 'Error sending message');
    }
  }, [isSendMassMessageSuccess, isSendMassMessageError, sendMassMessageError]);

  useEffect(() => {
    if (isSaveTemplateSuccess) {
      mixpanel.track(TrackableEvents.MassNotifyCreated, {
        user: userData?.profile?.sub,
        recipients: communication.recipients,
        message: communication.message,
        channel: communication.channel,
      });
      dispatch(openToast({ variant: 'success', header: 'Template saved' }));
      onFormChange({ ...communication, id: saveTemplateData.id, isTemplate: true });
      onSaveTemplate?.();
    } else if (isSaveTemplateError) {
      notify(saveTemplateError as ModelStateErrorResponse, 'Error saving template');
    }
  }, [isSaveTemplateSuccess, isSaveTemplateError, saveTemplateError]);

  useEffect(() => {
    if (isUpdateTemplateSuccess) {
      mixpanel.track(TrackableEvents.MassNotifyCreated, {
        user: userData?.profile?.sub,
        recipients: communication.recipients,
        message: communication.message,
        channel: communication.channel,
      });
      dispatch(openToast({ variant: 'success', header: 'Template updated' }));
      onFormChange({ ...communication, isTemplate: true });
      onSaveTemplate?.();
    } else if (isUpdateTemplateError) {
      notify(updateTemplateError as ModelStateErrorResponse, 'Error updating template');
    }
  }, [isUpdateTemplateSuccess, isUpdateTemplateError, updateTemplateError]);

  const handleSubmit = () => {
    sendMassMessage(communication);
    setSendOpenConfirmationModal(false);
    onSendMessage?.();
  };

  const handleClear = () => {
    onFormChange({
      id: undefined,
      recipients: [],
      headline: '',
      senderText: `${currentUser?.fullName || ''} at ${currentUser?.customerName} using Perry Weather`,
      message: '',
      overridePreferences: DEFAULT_OVERRIDE_PREFERENCE,
      channel: DEFAULT_CHANNEL,
    });
  };

  const handleMessageChange = (message: string) => {
    const newMessage = message.slice(0, 500);
    onFormChange({ ...communication, message: newMessage });
  };

  const handleHeadlineChange = (headline: string) => {
    const newHeadline = headline.slice(0, 100);
    onFormChange({ ...communication, headline: newHeadline });
  };

  const handleSenderTextChange = (senderText: string) => {
    const newSenderText = senderText.slice(0, 100);
    onFormChange({ ...communication, senderText: newSenderText });
  };

  const handleEmailChange = () => {
    let channel = communication.channel;
    channel = channel ^ (1 << EMAIL_BIT);
    onFormChange({ ...communication, channel: channel });
  };

  const handleOverridePreferenceChange = (override: boolean) => {
    onFormChange({ ...communication, overridePreferences: override });
  };

  const handleSave = async (value: string | undefined) => {
    if (!value) {
      return;
    }
    switch (value) {
      case 'save':
        await saveTemplate({ ...communication, recipients: [] });
        mixpanel.track(TrackableEvents.ButtonClicked, {
          button_location: 'Mass Notify',
          button_feature: 'Mass Notify',
          button_label: 'Save Template',
          button_type: 'button',
        });
        break;
      case 'override':
        await updateTemplate({ ...communication, recipients: [] });
        mixpanel.track(TrackableEvents.ButtonClicked, {
          button_location: 'Mass Notify',
          button_feature: 'Mass Notify',
          button_label: 'Override Template',
          button_type: 'button',
        });
        break;
    }
  };

  const handleSaveTemplate = async () => {
    await saveTemplate({ ...communication, recipients: [] });
  };

  const isBitSet = (source: number, targetBit: number) => (source & 0xff & (1 << targetBit)) !== 0;

  return (
    <Card sx={{ card: { overflow: 'visible' }, content: { textAlign: 'left' } }}>
      <Stack>
        <Typography variant='subtitle1'>Message</Typography>
        <Typography variant='subtitle2' color='textSecondary'>
          Headline
        </Typography>
        <TextField
          variant='outlined'
          size='small'
          value={communication.headline}
          // isError={errorState.Headline !== undefined}
          onChange={e => {
            handleHeadlineChange(e.target.value);
            updateError?.({ ...errorState, Headline: undefined });
          }}
        />
        {errorState.Headline && (
          <Typography textAlign='right' variant='overline' color='error'>
            {errorState.Headline[0]}
          </Typography>
        )}
        <div style={{ height: '20px' }} />
        <Typography variant='subtitle2' color='textSecondary'>
          Message
        </Typography>
        <TextField
          multiline
          rows={3}
          variant='outlined'
          size='small'
          value={communication.message}
          // isError={errorState.Message !== undefined}
          onChange={e => {
            handleMessageChange(e.target.value);
            updateError?.({ ...errorState, Message: undefined });
          }}
        />
        {errorState.Message ? (
          <Typography textAlign='right' variant='overline' color='error'>
            {errorState.Message[0]}
          </Typography>
        ) : (
          <Typography textAlign='right' variant='caption' color='textSecondary'>
            500 CHARACTER LIMIT
          </Typography>
        )}
      </Stack>

      <Typography variant='subtitle2' color='textSecondary'>
        Sent By
      </Typography>
      <TextField
        variant='outlined'
        size='small'
        value={communication.senderText}
        // isError={errorState.SenderText !== undefined}
        onChange={e => {
          handleSenderTextChange(e.target.value);
          updateError?.({ ...errorState, SenderText: undefined });
        }}
      />
      {errorState.SenderText && (
        <Typography textAlign='right' variant='overline' color='error'>
          {errorState.SenderText[0]}
        </Typography>
      )}

      <Divider style={{ margin: '24px 0' }} />

      <Stack>
        <Typography variant='subtitle1'>Delivery Settings</Typography>
        <Typography
          sx={{
            marginBottom: '8px',
          }}
          variant='subtitle2'
          color='textSecondary'>
          Choose who should receive the message, how it's sent, and whether to bypass user preferences.
        </Typography>

        <CheckboxSelect
          showCount
          showSelectAll
          maxItemsOnView={7}
          // error={errorState.Recipients !== undefined}
          placeholder='Recipients'
          options={recipOptions}
          selections={communication.recipients}
          sx={{
            container: { width: '100%' },
            menu: { width: 'fit-content', textWrap: 'nowrap', paddingRight: 3 },
            button: { backgroundColor: 'transparent' },
          }}
          onChange={e => {
            onFormChange({ ...communication, recipients: e });
            updateError?.({ ...errorState, Recipients: undefined });
          }}
        />

        <Grid
          item
          xs={12}
          sx={{
            margin: '8px',
          }}>
          <Stack direction='row' justifyContent='space-between' sx={{ alignItems: 'flex-start' }}>
            <Grid item xs={6}>
              <Checkbox
                disabled
                checked={isBitSet(communication.channel, SMS_BIT) || isBitSet(communication.channel, PUSH_BIT)}
                label={'Push/SMS'}
                sx={{
                  text: {
                    fontSize: '14px',
                  },
                }}
              />
              <TrackedCheckbox
                key={'email'}
                checked={isBitSet(communication.channel, EMAIL_BIT)}
                onChange={_ => handleEmailChange()}
                label={'Email'}
                sx={{
                  text: {
                    fontSize: '14px',
                  },
                }}
                mpExtraData={{ button_label: 'Email' }}
              />
            </Grid>
            <Grid item xs={6}>
              <Stack sx={{ justifyContent: 'flex-start' }}>
                <TrackedCheckbox
                  onChange={e => handleOverridePreferenceChange(e.target.checked)}
                  checked={communication.overridePreferences}
                  label='Override Notification Preferences'
                  sx={{ text: { fontSize: '14px' } }}
                  mpExtraData={{ button_label: 'Override User Preferences' }}
                />
                <Stack
                  direction='row'
                  gap={1}
                  sx={{ alignItems: 'center', visibility: communication.overridePreferences ? 'visible' : 'hidden' }}>
                  <Box sx={{ marginLeft: -0.35 }} display='flex' alignItems='center'>
                    <WarningIcon color={theme.palette.error.main} {...iconProps} />
                  </Box>
                  <Typography variant='caption' color='textSecondary'>
                    This message will be sent to all selected recipients, even if they are off-site or outside their
                    preferred schedule.
                  </Typography>
                </Stack>
              </Stack>
            </Grid>
          </Stack>
        </Grid>
      </Stack>

      <Divider style={{ margin: '24px 0' }} />

      <Stack gap={1}>
        <Typography variant='subtitle1'>Preview</Typography>

        <TextField
          disabled
          variant='outlined'
          size='small'
          value={communication.headline}
          sx={{
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: `${isDarkTheme ? '#767F8A' : '#ABB2B8'} !important`,
            },
            '& .MuiInputBase-root': {
              backgroundColor: isDarkTheme ? '#3C434E' : '#bbbdbf',
            },
            '& .Mui-disabled': {
              WebkitTextFillColor: theme.palette.text.primary,
              color: theme.palette.text.primary,
            },
          }}
        />
        <TextField
          disabled
          multiline
          rows={5}
          value={`${communication.message}\n\nSent By ${communication.senderText}`}
          sx={{
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: `${isDarkTheme ? '#767F8A' : '#ABB2B8'} !important`,
            },
            '& .MuiInputBase-root': {
              backgroundColor: isDarkTheme ? '#3C434E' : '#bbbdbf',
            },
            '& .Mui-disabled': {
              WebkitTextFillColor: `${theme.palette.text.primary} !important`,
              color: theme.palette.text.primary,
            },
          }}
        />
      </Stack>

      <Box marginTop='48px' display='flex' justifyContent='flex-end' gap={2}>
        <TrackedButton
          onClick={handleClear}
          disabled={isLoading}
          type='clear'
          sx={{ clear: { borderColor: theme.palette.error.main, gap: 1 } }}
          mpExtraData={{ button_label: 'Clear' }}>
          <Replay />
          <Typography variant='body1'>Clear</Typography>
        </TrackedButton>
        {communication.isTemplate ? (
          <>
            <ButtonGroup
              defaultOption={{
                label: 'Update Template',
                value: 'override',
              }}
              onButtonClick={handleSave}
              disabled={!hasTemplateBeenModified || !hasHeadlineAndMessageAndSender || isLoading}
              sx={{ container: { borderColor: theme.palette.border.input } }}
              options={[
                {
                  label: 'Save As New Template',
                  value: 'save',
                },
              ]}
              leftButtonChildren={<Save />}
            />
          </>
        ) : (
          <Button
            onClick={handleSaveTemplate}
            disabled={!hasHeadlineAndMessageAndSender || isLoading}
            type='clear'
            sx={{ clear: { borderColor: theme.palette.border.input, gap: 1 } }}>
            <Save />
            <Typography variant='body1'>Save as Template</Typography>
          </Button>
        )}
        <Button
          onClick={() => setSendOpenConfirmationModal(true)}
          disabled={!hasHeadlineAndMessageAndSender || !communication.recipients.length || isLoading}
          type='primary'
          sx={{ clear: { borderColor: theme.palette.border.input, gap: 1 } }}>
          <Typography variant='body1'>Send Message</Typography>
        </Button>
      </Box>
      <Modal
        disabled={false}
        modalState={{ open: openSendConfirmationModal, handleOpen: () => {}, handleClose: () => {} }}
        sx={{ paper: { backgroundColor: theme.palette.background.default } }}>
        <Stack>
          <Typography variant={'subtitle1'}>
            Confirm you would like to send this message to {communication.recipients.length} recipient
            {communication.recipients.length > 1 ? 's' : ''}?
          </Typography>
          <Stack direction='row' marginTop={4} gap={1} alignSelf='end'>
            <Button type='clear' onClick={() => setSendOpenConfirmationModal(false)}>
              <Typography variant='body1'>Cancel</Typography>
            </Button>
            <TrackedButton
              mpExtraData={{
                button_label: 'Send Message',
                recipients: communication.recipients,
                message: communication.message,
                channel: communication.channel,
                headline: communication.headline,
              }}
              onClick={handleSubmit}
              disabled={!hasHeadlineAndMessageAndSender || isLoading}>
              <Typography variant='body1'>Send Message</Typography>
            </TrackedButton>
          </Stack>
        </Stack>
      </Modal>
    </Card>
  );
};

export default MessageForm;
